import { useMemo, type ReactNode } from "react";
import { Modal } from "@/components/ui/Modal";
import type { ContratoDto, LocacaoDto, PeriodRange } from "@/services/locacoes";
import { formatNumber } from "@/utils/format";
import { formatHoras, formatPeriodLabel, locacoesTableHeadClass } from "./locacoesUtils";

export type ResumoKpiKind = "maquinas" | "semHora" | "litros" | "consumo";

export interface ResumoKpiLinha {
  id: string;
  maquina: string;
  cliente: string;
  contrato: string;
  obra: string;
  horas: number;
  litros: number;
  litrosPorHora: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  kind: ResumoKpiKind;
  period: PeriodRange;
  value: string;
  maquinaNome?: string;
  linhas: ResumoKpiLinha[];
}

const TITLES: Record<ResumoKpiKind, string> = {
  maquinas: "Máquinas que trabalharam",
  semHora: "Locadas sem hora",
  litros: "Litros abastecidos",
  consumo: "Consumo médio",
};

function share(part: number, total: number): string {
  if (total <= 0) return "—";
  return `${Math.round((part / total) * 1000) / 10}%`;
}

export function contratoClienteDaMaquina(
  maquinaId: string,
  locacoes: LocacaoDto[],
  contratos: ContratoDto[],
): { cliente: string; contrato: string; obra: string } {
  const locacao = locacoes
    .filter((l) => l.status === "ativa" && l.maquinaId === maquinaId)
    .sort((a, b) => b.dataInicio.localeCompare(a.dataInicio))[0];
  const contrato = locacao?.contratoId
    ? contratos.find((c) => c.id === locacao.contratoId)
    : contratos.find((c) => c.status === "ativo" && c.maquinaIds.includes(maquinaId));
  const cliente = (contrato?.clienteSiengeNome || "").trim() || "Sem cliente";
  const codigo = contrato?.codigoContrato || locacao?.contratoLabel || "Sem contrato";
  const obra = (locacao?.obraNome || locacao?.costCenterLabel || contrato?.costCenterLabel || "").trim() || "—";
  return { cliente, contrato: codigo, obra };
}

export function ResumoKpiDetalheModal({
  open,
  onClose,
  kind,
  period,
  value,
  maquinaNome,
  linhas,
}: Props) {
  const detalhe = useMemo(() => {
    const porClienteMap = new Map<string, { cliente: string; contratos: Set<string>; maquinas: number; horas: number; litros: number }>();
    const porContratoMap = new Map<string, { contrato: string; cliente: string; maquinas: number; horas: number; litros: number }>();
    for (const row of linhas) {
      const cliente = porClienteMap.get(row.cliente) ?? {
        cliente: row.cliente,
        contratos: new Set<string>(),
        maquinas: 0,
        horas: 0,
        litros: 0,
      };
      cliente.contratos.add(row.contrato);
      cliente.maquinas += 1;
      cliente.horas += row.horas;
      cliente.litros += row.litros;
      porClienteMap.set(row.cliente, cliente);

      const key = `${row.contrato}|${row.cliente}`;
      const contrato = porContratoMap.get(key) ?? {
        contrato: row.contrato,
        cliente: row.cliente,
        maquinas: 0,
        horas: 0,
        litros: 0,
      };
      contrato.maquinas += 1;
      contrato.horas += row.horas;
      contrato.litros += row.litros;
      porContratoMap.set(key, contrato);
    }
    const sortKey = kind === "litros" || kind === "consumo" ? "litros" : "horas";
    const porCliente = [...porClienteMap.values()]
      .map((row) => ({ ...row, contratos: row.contratos.size }))
      .sort((a, b) => b[sortKey] - a[sortKey] || a.cliente.localeCompare(b.cliente, "pt-BR"));
    const porContrato = [...porContratoMap.values()].sort(
      (a, b) => b[sortKey] - a[sortKey] || a.contrato.localeCompare(b.contrato, "pt-BR"),
    );
    const totalHoras = linhas.reduce((s, row) => s + row.horas, 0);
    const totalLitros = linhas.reduce((s, row) => s + row.litros, 0);
    return { porCliente, porContrato, totalHoras, totalLitros };
  }, [kind, linhas]);

  const showHoras = kind !== "semHora";
  const showLitros = kind === "litros" || kind === "consumo";
  const emptyLabel =
    kind === "semHora"
      ? "Nenhuma máquina locada ficou sem apontamento neste período."
      : kind === "maquinas"
        ? "Nenhuma máquina apontou hora neste período."
        : "Nenhum contrato ou cliente neste indicador.";

  return (
    <Modal open={open} onClose={onClose} size="xl" closeOnEscape closeOnBackdropClick title={TITLES[kind]}>
      <div className="flex flex-col gap-5">
        <div>
          <p className="text-2xl font-bold tabular-nums text-slate-800">{value}</p>
          <p className="mt-1 text-sm text-slate-500">
            {formatPeriodLabel(period)}
            {maquinaNome ? ` · ${maquinaNome}` : ""}
          </p>
        </div>

        {linhas.length === 0 ? (
          <p className="py-8 text-center text-sm text-slate-500">{emptyLabel}</p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Por cliente</h3>
                <GridTable>
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-3 py-2 text-left">Cliente</th>
                      <th className="px-3 py-2 text-right">Contratos</th>
                      <th className="px-3 py-2 text-right">Máquinas</th>
                      {showHoras ? <th className="px-3 py-2 text-right">Horas</th> : null}
                      {showLitros ? <th className="px-3 py-2 text-right">Litros</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detalhe.porCliente.map((row) => (
                      <tr key={row.cliente}>
                        <td className="px-3 py-2 text-slate-700">{row.cliente}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.contratos}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.maquinas}</td>
                        {showHoras ? (
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatHoras(row.horas)}
                          </td>
                        ) : null}
                        {showLitros ? (
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatNumber(row.litros, 0)}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </GridTable>
              </section>

              <section>
                <h3 className="mb-2 text-sm font-semibold text-slate-800">Por contrato</h3>
                <GridTable>
                  <thead className={locacoesTableHeadClass}>
                    <tr>
                      <th className="px-3 py-2 text-left">Contrato</th>
                      <th className="px-3 py-2 text-right">Máquinas</th>
                      {showHoras ? <th className="px-3 py-2 text-right">Horas</th> : null}
                      {showLitros ? <th className="px-3 py-2 text-right">Litros</th> : null}
                      {showLitros ? <th className="px-3 py-2 text-right">Parte</th> : null}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {detalhe.porContrato.map((row) => (
                      <tr key={`${row.contrato}-${row.cliente}`}>
                        <td className="px-3 py-2">
                          <p className="font-medium text-slate-800">{row.contrato}</p>
                          <p className="text-xs text-slate-500">{row.cliente}</p>
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-600">{row.maquinas}</td>
                        {showHoras ? (
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatHoras(row.horas)}
                          </td>
                        ) : null}
                        {showLitros ? (
                          <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                            {formatNumber(row.litros, 0)}
                          </td>
                        ) : null}
                        {showLitros ? (
                          <td className="px-3 py-2 text-right tabular-nums text-slate-500">
                            {share(row.litros, detalhe.totalLitros)}
                          </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </GridTable>
              </section>
            </div>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-slate-800">Máquinas</h3>
              <GridTable>
                <thead className={locacoesTableHeadClass}>
                  <tr>
                    <th className="px-3 py-2 text-left">Máquina</th>
                    <th className="px-3 py-2 text-left">Cliente / contrato</th>
                    <th className="px-3 py-2 text-left">Obra</th>
                    {showHoras ? <th className="px-3 py-2 text-right">Horas</th> : null}
                    {showLitros ? <th className="px-3 py-2 text-right">Litros</th> : null}
                    {kind === "consumo" ? <th className="px-3 py-2 text-right">L/h</th> : null}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {linhas.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-2 font-medium text-slate-800">{row.maquina}</td>
                      <td className="px-3 py-2">
                        <p className="text-slate-800">{row.cliente}</p>
                        <p className="text-xs text-slate-500">{row.contrato}</p>
                      </td>
                      <td className="max-w-[12rem] px-3 py-2 text-slate-600">{row.obra}</td>
                      {showHoras ? (
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                          {formatHoras(row.horas)}
                        </td>
                      ) : null}
                      {showLitros ? (
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                          {formatNumber(row.litros, 0)}
                        </td>
                      ) : null}
                      {kind === "consumo" ? (
                        <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                          {row.horas > 0 || row.litros > 0 ? formatNumber(row.litrosPorHora, 1) : "—"}
                        </td>
                      ) : null}
                    </tr>
                  ))}
                </tbody>
              </GridTable>
            </section>
          </>
        )}
      </div>
    </Modal>
  );
}

function GridTable({ children }: { children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  );
}
